import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import SelectFarmWarehouse, { getAOCWarehouses } from "../../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";
import { checkAccess } from "../../../utils/roleFunctions";
import { ACCESS, ACCESS_SECTIONS } from "../../../utils/constants/access";

const NULL_FILTER_TOKEN = "__NULL__";
const MIXED_SUPPLIER_FILTER_TOKEN = "__MIXED__";

const PERIOD_OPTIONS = [
    { id: "day", name: "Day" },
    { id: "week", name: "Week" },
    { id: "month", name: "Month" },
    { id: "year", name: "Year" },
];

const DISPLAY_UNIT_OPTIONS = [
    { id: "doz", label: "Doz" },
    { id: "pcs", label: "Pcs" },
];

const DOZEN_SIZE = 12;

const CHART_COLOURS = [
    { background: "rgba(21, 101, 192, 0.78)", border: "rgba(21, 101, 192, 1)" },
    { background: "rgba(46, 125, 50, 0.78)", border: "rgba(46, 125, 50, 1)" },
    { background: "rgba(239, 108, 0, 0.78)", border: "rgba(239, 108, 0, 1)" },
    { background: "rgba(123, 31, 162, 0.78)", border: "rgba(123, 31, 162, 1)" },
    { background: "rgba(2, 136, 209, 0.78)", border: "rgba(2, 136, 209, 1)" },
    { background: "rgba(173, 20, 87, 0.78)", border: "rgba(173, 20, 87, 1)" },
    { background: "rgba(85, 139, 47, 0.78)", border: "rgba(85, 139, 47, 1)" },
    { background: "rgba(251, 140, 0, 0.78)", border: "rgba(251, 140, 0, 1)" },
    { background: "rgba(0, 121, 107, 0.78)", border: "rgba(0, 121, 107, 1)" },
    { background: "rgba(109, 76, 65, 0.78)", border: "rgba(109, 76, 65, 1)" },
];

const emptyFilterOptions = {
    crops: [],
    suppliers: [],
    stockTypes: [],
    cropDates: [],
};

const emptyFilterBatches = [];

const emptyFilters = {
    cropId: "",
    supplierId: "",
    stockType: "",
    cropDate: "",
};

const emptyReport = {
    periodType: "month",
    selectedBatchCount: 0,
    receivalColumns: [],
    receivalPeriods: [],
    backToWaterColumns: [],
    backToWaterPeriods: [],
    transferColumns: [],
    transferPeriods: [],
    untrackedRemovalColumns: [],
    untrackedRemovalPeriods: [],
    gradeColumns: [],
    gradePeriods: [],
    variancePeriods: [],
    offSlatsPeriods: [],
    mixedColumns: [],
    mixedPeriods: [],
};

const parseDateValue = (value) => {
    if (!value) {
        return null;
    }

    const trimmedValue = `${value}`.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    const dateValue = new Date(trimmedValue);
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const formatDisplayDate = (value) => {
    if (!value) {
        return "";
    }

    const trimmedValue = `${value}`.trim();
    const dateValue = parseDateValue(trimmedValue);

    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return trimmedValue;
    }

    return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(dateValue);
};

const getDisplayUnitLabel = (displayUnit) => (displayUnit === "doz" ? "doz" : "pcs");

const getDisplayUnitName = (displayUnit) => (displayUnit === "doz" ? "Doz" : "Pcs");

const convertQuantityForDisplay = (value, displayUnit) => {
    const numericValue = Number(value) || 0;

    if (displayUnit !== "doz") {
        return numericValue;
    }

    const convertedValue = numericValue / DOZEN_SIZE;
    return Math.abs(convertedValue) < 0.005 ? 0 : Number(convertedValue.toFixed(2));
};

const formatDisplayQuantity = (value, displayUnit) => {
    return numberFormat(convertQuantityForDisplay(value, displayUnit));
};

const formatOysterCount = (value, displayUnit) => {
    return `${formatDisplayQuantity(value, displayUnit)} ${getDisplayUnitLabel(displayUnit)}`;
};

const escapeCsvValue = (value) => {
    const stringValue = `${value ?? ""}`;

    if (
        stringValue.includes('"')
        || stringValue.includes(",")
        || stringValue.includes("\n")
        || stringValue.includes("\r")
    ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
};

const downloadCsvFile = (rows, fileName) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return;
    }

    const csv = rows
        .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
        .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};

const buildBatchReportTableRows = (periods, columns, displayUnit) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return [];
    }

    const unitLabel = getDisplayUnitLabel(displayUnit);

    return [
        [
            "Period",
            "Start Date",
            "End Date",
            ...columns.map((column) => `${column.grade} (${unitLabel})`),
            `Total (${unitLabel})`,
        ],
        ...periods.map((period) => ([
            period.periodLabel || period.periodKey,
            formatDisplayDate(period.startDate),
            formatDisplayDate(period.endDate),
            ...columns.map((column) => formatDisplayQuantity(getPeriodTotalValue(period, column.gradeId), displayUnit)),
            formatDisplayQuantity(period.totalOysters, displayUnit),
        ])),
    ];
};

const buildBatchReportExportFileName = (sectionKey, periodType, displayUnit) => {
    const dateStamp = new Date().toISOString().slice(0, 10);
    const periodLabel = getPeriodTypeLabel(periodType).replace(/\s+/g, "");
    return `BatchReport_${sectionKey}_${periodLabel}_${getDisplayUnitName(displayUnit)}_${dateStamp}.csv`;
};

const getCropLabel = (crop) => {
    if (!crop) {
        return "";
    }

    return crop.id === NULL_FILTER_TOKEN ? "No crop" : crop.name;
};

const getPeriodTypeLabel = (value) => {
    return PERIOD_OPTIONS.find((option) => option.id === value)?.name || "Month";
};

const normaliseStringOptions = (values, preserveNullOption = false) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, value) => {
        const nextValue = value == null || `${value}`.trim() === ""
            ? (preserveNullOption ? NULL_FILTER_TOKEN : null)
            : `${value}`;

        if (!nextValue || seen.has(nextValue)) {
            return accumulator;
        }

        seen.add(nextValue);
        accumulator.push(nextValue);
        return accumulator;
    }, []);
};

const sortCropOptions = (values) => {
    return [...values].sort((left, right) => {
        const leftIsNull = left.id === NULL_FILTER_TOKEN;
        const rightIsNull = right.id === NULL_FILTER_TOKEN;

        if (leftIsNull !== rightIsNull) {
            return leftIsNull ? 1 : -1;
        }

        return `${left.name}`.localeCompare(`${right.name}`, undefined, { numeric: true, sensitivity: "base" });
    });
};

const normaliseBatchOptions = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, option) => {
        if (!option) {
            return accumulator;
        }

        const cropId = option.cropId == null || `${option.cropId}`.trim() === ""
            ? NULL_FILTER_TOKEN
            : `${option.cropId}`.trim();
        const cropName = option.cropName == null || `${option.cropName}`.trim() === ""
            ? (cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${cropId}`)
            : `${option.cropName}`.trim();
        const supplierId = option.supplierId == null ? "" : `${option.supplierId}`;
        const supplierKey = option.supplierKey == null || `${option.supplierKey}`.trim() === ""
            ? supplierId
            : `${option.supplierKey}`.trim();
        const stockType = option.stockType == null ? "" : `${option.stockType}`.trim();
        const stockTypeLabel = option.stockTypeLabel || stockType;
        const cropDate = option.cropDate == null ? "" : `${option.cropDate}`.trim();

        if (!supplierKey || !stockType || !cropDate) {
            return accumulator;
        }

        const key = `${cropId}|${supplierKey}|${stockType}|${cropDate}`;
        if (seen.has(key)) {
            return accumulator;
        }

        seen.add(key);

        accumulator.push({
            batch: option.batch || "",
            cropId,
            cropName,
            supplierId: supplierKey,
            supplierName: option.supplierName || "Unnamed supplier",
            stockType,
            stockTypeLabel,
            cropDate,
        });

        return accumulator;
    }, []);
};

const matchesBatchFilters = (batch, filters, ignoredField) => {
    if (!batch) {
        return false;
    }

    return ["cropId", "supplierId", "stockType", "cropDate"].every((field) => {
        if (field === ignoredField) {
            return true;
        }

        return !filters[field] || batch[field] === filters[field];
    });
};

const buildSupplierOptions = (batches) => {
    const suppliers = new Map();

    batches.forEach((batch) => {
        if (!batch?.supplierId) {
            return;
        }

        const isMixedSupplier = batch.supplierId === MIXED_SUPPLIER_FILTER_TOKEN
            || `${batch.supplierName || ""}`.trim().toLowerCase() === "mixed suppliers";
        const nextName = isMixedSupplier
            ? "Mixed Suppliers"
            : (batch.supplierName || "Unnamed supplier");
        const existing = suppliers.get(batch.supplierId);

        if (!existing || (existing.name !== "Mixed Suppliers" && nextName === "Mixed Suppliers")) {
            suppliers.set(batch.supplierId, { id: batch.supplierId, name: nextName });
        }
    });

    return Array.from(suppliers.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const buildStockTypeOptions = (batches) => {
    const stockTypes = new Map();
    const getSortOrder = (value) => (value === "WLD" ? 0 : value === "HAT" ? 1 : 2);

    batches.forEach((batch) => {
        if (!batch?.stockType || stockTypes.has(batch.stockType)) {
            return;
        }

        stockTypes.set(batch.stockType, {
            id: batch.stockType,
            name: batch.stockTypeLabel || batch.stockType,
        });
    });

    return Array.from(stockTypes.values()).sort((left, right) => getSortOrder(left.id) - getSortOrder(right.id));
};

const buildFilterOptions = (batches, filters) => {
    if (!Array.isArray(batches) || batches.length === 0) {
        return emptyFilterOptions;
    }

    const getMatchingBatches = (ignoredField) => batches.filter((batch) => matchesBatchFilters(batch, filters, ignoredField));

    const cropMap = new Map();
    getMatchingBatches("cropId").forEach((batch) => {
        if (!batch?.cropId || cropMap.has(batch.cropId)) {
            return;
        }

        cropMap.set(batch.cropId, {
            id: batch.cropId,
            name: batch.cropName || (batch.cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${batch.cropId}`),
        });
    });

    return {
        crops: sortCropOptions(Array.from(cropMap.values())),
        suppliers: buildSupplierOptions(getMatchingBatches("supplierId")),
        stockTypes: buildStockTypeOptions(getMatchingBatches("stockType")),
        cropDates: normaliseStringOptions(getMatchingBatches("cropDate").map((batch) => batch.cropDate)),
    };
};

const sanitiseFilters = (filters, filterOptions) => {
    const nextFilters = { ...filters };

    if (nextFilters.cropId && !filterOptions.crops.some((option) => option.id === nextFilters.cropId)) {
        nextFilters.cropId = "";
    }

    if (nextFilters.supplierId && !filterOptions.suppliers.some((option) => option.id === nextFilters.supplierId)) {
        nextFilters.supplierId = "";
    }

    if (nextFilters.stockType && !filterOptions.stockTypes.some((option) => option.id === nextFilters.stockType)) {
        nextFilters.stockType = "";
    }

    if (nextFilters.cropDate && !filterOptions.cropDates.includes(nextFilters.cropDate)) {
        nextFilters.cropDate = "";
    }

    return nextFilters;
};

const areFiltersEqual = (left, right) => (
    left.cropId === right.cropId
    && left.supplierId === right.supplierId
    && left.stockType === right.stockType
    && left.cropDate === right.cropDate
);

const resolveFilterState = (batches, filters) => {
    let nextFilters = { ...filters };

    while (true) {
        const nextOptions = buildFilterOptions(batches, nextFilters);
        const sanitised = sanitiseFilters(nextFilters, nextOptions);

        if (areFiltersEqual(nextFilters, sanitised)) {
            return {
                filters: sanitised,
                options: nextOptions,
            };
        }

        nextFilters = sanitised;
    }
};

const normaliseReportSection = (periods, columns) => {
    const safeColumns = Array.isArray(columns) ? columns : [];
    const safePeriods = Array.isArray(periods) ? periods : [];

    return {
        columns: safeColumns,
        periods: safePeriods.map((period) => ({
            periodKey: period?.periodKey || "",
            periodLabel: period?.periodLabel || "",
            startDate: period?.startDate || "",
            endDate: period?.endDate || "",
            totalOysters: Number(period?.totalOysters) || 0,
            totals: Array.isArray(period?.totals)
                ? period.totals.map((total) => ({
                    gradeId: total?.gradeId,
                    grade: total?.grade || "",
                    oysterCount: Number(total?.oysterCount) || 0,
                }))
                : [],
        })),
    };
};

const normaliseReport = (data) => {
    const receivalSection = normaliseReportSection(data?.receivalPeriods, data?.receivalColumns);
    const backToWaterSection = normaliseReportSection(data?.backToWaterPeriods, data?.backToWaterColumns);
    const transferSection = normaliseReportSection(data?.transferPeriods, data?.transferColumns);
    const untrackedRemovalSection = normaliseReportSection(data?.untrackedRemovalPeriods, data?.untrackedRemovalColumns);
    const gradeSection = normaliseReportSection(data?.gradePeriods, data?.gradeColumns);
    const varianceSection = normaliseReportSection(data?.variancePeriods, []);
    const offSlatsSection = normaliseReportSection(data?.offSlatsPeriods, []);
    const mixedSection = normaliseReportSection(data?.mixedPeriods, data?.mixedColumns);

    return {
        periodType: data?.periodType || "month",
        selectedBatchCount: Number(data?.selectedBatchCount) || 0,
        receivalColumns: receivalSection.columns,
        receivalPeriods: receivalSection.periods,
        backToWaterColumns: backToWaterSection.columns,
        backToWaterPeriods: backToWaterSection.periods,
        transferColumns: transferSection.columns,
        transferPeriods: transferSection.periods,
        untrackedRemovalColumns: untrackedRemovalSection.columns,
        untrackedRemovalPeriods: untrackedRemovalSection.periods,
        gradeColumns: gradeSection.columns,
        gradePeriods: gradeSection.periods,
        variancePeriods: varianceSection.periods,
        offSlatsPeriods: offSlatsSection.periods,
        mixedColumns: mixedSection.columns,
        mixedPeriods: mixedSection.periods,
    };
};

const getPeriodTotalValue = (period, gradeId) => {
    return period?.totals?.find((item) => item.gradeId === gradeId)?.oysterCount || 0;
};

const hasRenderableSectionData = (periods, columns) => {
    if (!Array.isArray(periods) || periods.length === 0 || !Array.isArray(columns) || columns.length === 0) {
        return false;
    }

    return periods.some((period) => columns.some((column) => getPeriodTotalValue(period, column.gradeId) > 0));
};

const hasRenderablePeriodTotals = (periods) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return false;
    }

    return periods.some((period) => Math.abs(Number(period?.totalOysters) || 0) > 0);
};

const buildStackedChartData = (periods, columns) => {
    return {
        labels: periods.map((period) => period.periodLabel || period.periodKey),
        datasets: columns.map((column, index) => {
            const colours = CHART_COLOURS[index % CHART_COLOURS.length];

            return {
                label: column.grade,
                data: periods.map((period) => getPeriodTotalValue(period, column.gradeId)),
                backgroundColor: colours.background,
                borderColor: colours.border,
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 56,
            };
        }),
    };
};

const buildTotalChartData = (periods, label) => {
    const colours = CHART_COLOURS[0];

    return {
        labels: periods.map((period) => period.periodLabel || period.periodKey),
        datasets: [
            {
                label,
                data: periods.map((period) => Number(period?.totalOysters) || 0),
                backgroundColor: colours.background,
                borderColor: colours.border,
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 56,
            },
        ],
    };
};

const buildStockVsTransfersColumns = (report) => {
    const columnsByGradeId = new Map();

    [...(report?.gradeColumns || []), ...(report?.transferColumns || [])].forEach((column) => {
        if (column?.gradeId == null || columnsByGradeId.has(column.gradeId)) {
            return;
        }

        columnsByGradeId.set(column.gradeId, {
            gradeId: column.gradeId,
            grade: column.grade || "Unknown",
        });
    });

    return Array.from(columnsByGradeId.values())
        .sort((left, right) => `${left.grade}`.localeCompare(`${right.grade}`, undefined, { sensitivity: "base" }));
};

const buildStockVsTransfersPeriods = (report, columns) => {
    const allPeriods = [...(report?.gradePeriods || []), ...(report?.transferPeriods || [])];
    const orderedPeriodsByKey = new Map();

    allPeriods.forEach((period, index) => {
        const periodKey = period?.periodKey || `${period?.periodLabel || "Period"}-${index}`;
        if (orderedPeriodsByKey.has(periodKey)) {
            return;
        }

        orderedPeriodsByKey.set(periodKey, {
            periodKey,
            periodLabel: period?.periodLabel || periodKey,
            startDate: period?.startDate || "",
            endDate: period?.endDate || "",
        });
    });

    const orderedPeriods = Array.from(orderedPeriodsByKey.values())
        .sort((left, right) => {
            const leftDate = parseDateValue(left.startDate) || parseDateValue(left.endDate);
            const rightDate = parseDateValue(right.startDate) || parseDateValue(right.endDate);

            if (leftDate && rightDate && leftDate.getTime() !== rightDate.getTime()) {
                return leftDate.getTime() - rightDate.getTime();
            }

            if (leftDate && !rightDate) {
                return -1;
            }

            if (!leftDate && rightDate) {
                return 1;
            }

            return `${left.periodKey}`.localeCompare(`${right.periodKey}`, undefined, { numeric: true, sensitivity: "base" });
        });

    const gradePeriodsByKey = new Map((report?.gradePeriods || []).map((period) => [period.periodKey, period]));
    const transferPeriodsByKey = new Map((report?.transferPeriods || []).map((period) => [period.periodKey, period]));
    const cumulativeTransferTotalsByGradeId = new Map();

    return orderedPeriods.map((period) => {
        const stockPeriod = gradePeriodsByKey.get(period.periodKey);
        const transferPeriod = transferPeriodsByKey.get(period.periodKey);

        columns.forEach((column) => {
            const nextTransferValue = getPeriodTotalValue(transferPeriod, column.gradeId);
            if (nextTransferValue === 0) {
                return;
            }

            cumulativeTransferTotalsByGradeId.set(
                column.gradeId,
                (cumulativeTransferTotalsByGradeId.get(column.gradeId) || 0) + nextTransferValue
            );
        });

        const stockTotals = columns.map((column) => ({
            gradeId: column.gradeId,
            grade: column.grade,
            oysterCount: getPeriodTotalValue(stockPeriod, column.gradeId),
        }));

        const transferTotals = columns.map((column) => ({
            gradeId: column.gradeId,
            grade: column.grade,
            oysterCount: cumulativeTransferTotalsByGradeId.get(column.gradeId) || 0,
        }));

        return {
            periodKey: period.periodKey,
            periodLabel: period.periodLabel,
            startDate: period.startDate,
            endDate: period.endDate,
            stockTotalOysters: stockTotals.reduce((sum, total) => sum + total.oysterCount, 0),
            transferTotalOysters: transferTotals.reduce((sum, total) => sum + total.oysterCount, 0),
            stockTotals,
            transferTotals,
        };
    });
};

const hasRenderableStockVsTransfersTotals = (periods) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return false;
    }

    return periods.some((period) => period.stockTotalOysters > 0 || period.transferTotalOysters > 0);
};

const buildStockVsTransfersChartData = (periods, columns) => ({
    labels: periods.map((period) => period.periodLabel || period.periodKey),
    datasets: columns.flatMap((column, index) => {
        const colours = CHART_COLOURS[index % CHART_COLOURS.length];
        const transferBackground = colours.background.replace("0.78", "0.36");

        return [
            {
                label: `${column.grade} Stock`,
                data: periods.map((period) => period.stockTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0),
                backgroundColor: colours.background,
                borderColor: colours.border,
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 36,
                stack: "stock",
            },
            {
                label: `${column.grade} Transfers`,
                data: periods.map((period) => period.transferTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0),
                backgroundColor: transferBackground,
                borderColor: colours.border,
                borderWidth: 1,
                borderRadius: 4,
                maxBarThickness: 36,
                stack: "transfers",
            },
        ];
    }),
});

const buildStockVsTransfersTableRows = (periods, columns, displayUnit) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return [];
    }

    const unitLabel = getDisplayUnitLabel(displayUnit);

    return [
        [
            "Period",
            "Start Date",
            "End Date",
            `Stock Total (${unitLabel})`,
            `Cumulative Transfers (${unitLabel})`,
            ...columns.flatMap((column) => [`Stock - ${column.grade} (${unitLabel})`, `Transfers - ${column.grade} (${unitLabel})`]),
        ],
        ...periods.map((period) => ([
            period.periodLabel || period.periodKey,
            formatDisplayDate(period.startDate),
            formatDisplayDate(period.endDate),
            formatDisplayQuantity(period.stockTotalOysters, displayUnit),
            formatDisplayQuantity(period.transferTotalOysters, displayUnit),
            ...columns.flatMap((column) => [
                formatDisplayQuantity(period.stockTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0, displayUnit),
                formatDisplayQuantity(period.transferTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0, displayUnit),
            ]),
        ])),
    ];
};

const sumBatchReportPeriodTotals = (periods) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return 0;
    }

    return periods.reduce((sum, period) => sum + (Number(period?.totalOysters) || 0), 0);
};

const getLatestBatchReportPeriod = (periods) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return null;
    }

    return periods[periods.length - 1] || null;
};

const buildBatchReportTopSummaryRows = (report) => {
    const totalGradingVariance = sumBatchReportPeriodTotals(report?.variancePeriods);
    const totalOffSlats = sumBatchReportPeriodTotals(report?.offSlatsPeriods);
    const totalInSegments = [
        {
            id: "newStock",
            label: "New Stock",
            value: sumBatchReportPeriodTotals(report?.receivalPeriods),
            backgroundColor: CHART_COLOURS[1].background,
            borderColor: CHART_COLOURS[1].border,
        },
        {
            id: "backToWater",
            label: "Back to Water",
            value: sumBatchReportPeriodTotals(report?.backToWaterPeriods),
            backgroundColor: CHART_COLOURS[4].background,
            borderColor: CHART_COLOURS[4].border,
        },
        {
            id: "gradingVariancePositive",
            label: "Grading Variance",
            value: Math.max(totalGradingVariance, 0),
            backgroundColor: CHART_COLOURS[3].background,
            borderColor: CHART_COLOURS[3].border,
        },
        {
            id: "offSlatsPositive",
            label: "Off Slats",
            value: Math.max(totalOffSlats, 0),
            backgroundColor: CHART_COLOURS[7].background,
            borderColor: CHART_COLOURS[7].border,
        },
    ].filter((segment) => segment.value > 0);

    const totalOutSegments = [
        {
            id: "transfers",
            label: "Transfers",
            value: sumBatchReportPeriodTotals(report?.transferPeriods),
            backgroundColor: CHART_COLOURS[2].background,
            borderColor: CHART_COLOURS[2].border,
        },
        {
            id: "untrackedRemovals",
            label: "Untracked Removals",
            value: sumBatchReportPeriodTotals(report?.untrackedRemovalPeriods),
            backgroundColor: CHART_COLOURS[6].background,
            borderColor: CHART_COLOURS[6].border,
        },
        {
            id: "mixed",
            label: "Mixed",
            value: sumBatchReportPeriodTotals(report?.mixedPeriods),
            backgroundColor: CHART_COLOURS[5].background,
            borderColor: CHART_COLOURS[5].border,
        },
        {
            id: "gradingVariance",
            label: "Grading Variance",
            value: Math.max(-totalGradingVariance, 0),
            backgroundColor: CHART_COLOURS[3].background,
            borderColor: CHART_COLOURS[3].border,
        },
        {
            id: "offSlats",
            label: "Off Slats",
            value: Math.max(-totalOffSlats, 0),
            backgroundColor: CHART_COLOURS[7].background,
            borderColor: CHART_COLOURS[7].border,
        },
    ].filter((segment) => segment.value > 0);

    const latestGradePeriod = getLatestBatchReportPeriod(report?.gradePeriods);
    const currentStockSegments = Array.isArray(latestGradePeriod?.totals)
        ? latestGradePeriod.totals
            .map((total, index) => ({
                id: `grade-${total?.gradeId ?? index}`,
                label: total?.grade || "Unknown",
                value: Number(total?.oysterCount) || 0,
                backgroundColor: CHART_COLOURS[index % CHART_COLOURS.length].background,
                borderColor: CHART_COLOURS[index % CHART_COLOURS.length].border,
            }))
            .filter((segment) => segment.value > 0)
        : [];

    return [
        {
            id: "totalIn",
            label: "Total In",
            description: "New Stock, Back to Water, and any positive Grading Variance across the loaded timeline.",
            total: totalInSegments.reduce((sum, segment) => sum + segment.value, 0),
            segments: totalInSegments,
        },
        {
            id: "totalOut",
            label: "Total Out",
            description: "Transfers, Untracked Removals, Mixed, and any negative Grading Variance across the loaded timeline.",
            total: totalOutSegments.reduce((sum, segment) => sum + segment.value, 0),
            segments: totalOutSegments,
        },
        {
            id: "currentStock",
            label: "Current Stock",
            description: latestGradePeriod
                ? `The latest batch total snapshot${latestGradePeriod.periodLabel ? ` for ${latestGradePeriod.periodLabel}` : ""}.`
                : "The latest batch total snapshot.",
            total: Number(latestGradePeriod?.totalOysters) || 0,
            segments: currentStockSegments,
        },
    ];
};

const hasBatchReportTopSummaryData = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return false;
    }

    return rows.some((row) => row.total > 0 && Array.isArray(row.segments) && row.segments.length > 0);
};

const BatchReportTopSummary = ({ rows, emptyMessage, displayUnit, showMasterDetail, masterReconciliation }) => {
    if (!hasBatchReportTopSummaryData(rows)) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    const maxTotal = rows.reduce((max, row) => Math.max(max, Number(row?.total) || 0), 0);

    return (
        <Stack spacing={2}>
            {rows.map((row) => {
                const rowTotal = Number(row?.total) || 0;
                const segments = Array.isArray(row?.segments) ? row.segments.filter((segment) => segment.value > 0) : [];

                return (
                    <Box
                        key={row.id}
                        sx={{
                            backgroundColor: "rgba(245, 247, 250, 0.9)",
                            border: "1px solid rgba(0, 0, 0, 0.08)",
                            borderRadius: 2,
                            p: 2,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <Typography variant="h6" mb={0}>
                                    {row.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={0}>
                                    {row.description}
                                </Typography>
                                {showMasterDetail && row.id === "totalOut" && masterReconciliation ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                                        Net Flow (Total In - Total Out): {formatDisplayQuantity(masterReconciliation.netFlow, displayUnit)} {getDisplayUnitLabel(displayUnit)} | Matches Current Stock: {masterReconciliation.matchesCurrentStock ? "Yes" : "No"}
                                    </Typography>
                                ) : null}
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box
                                    sx={{
                                        height: 28,
                                        overflow: "hidden",
                                        borderRadius: 999,
                                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                                        border: "1px solid rgba(0, 0, 0, 0.1)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            height: "100%",
                                            width: `${maxTotal > 0 ? (rowTotal / maxTotal) * 100 : 0}%`,
                                            minWidth: rowTotal > 0 ? 12 : 0,
                                        }}
                                    >
                                        {segments.map((segment) => {
                                            const width = rowTotal > 0 ? (segment.value / rowTotal) * 100 : 0;
                                            return (
                                                <Box
                                                    key={segment.id}
                                                    sx={{
                                                        width: `${width}%`,
                                                        minWidth: width > 0 ? 4 : 0,
                                                        backgroundColor: segment.backgroundColor,
                                                        borderRight: "1px solid rgba(255, 255, 255, 0.35)",
                                                    }}
                                                    title={`${segment.label}: ${formatOysterCount(segment.value, displayUnit)}`}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                    {segments.map((segment) => (
                                        <Stack key={`${row.id}-${segment.id}-legend`} direction="row" spacing={0.75} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    backgroundColor: segment.backgroundColor,
                                                    border: `1px solid ${segment.borderColor}`,
                                                }}
                                            />
                                            <Typography variant="caption" mb={0}>
                                                {segment.label}: {formatDisplayQuantity(segment.value, displayUnit)}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Typography variant="h5" mb={0} align="right">
                                    {formatDisplayQuantity(rowTotal, displayUnit)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={0} align="right">
                                    {getDisplayUnitLabel(displayUnit)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Stack>
    );
};

const BatchReportStackedChart = ({ periods, columns, emptyMessage, displayUnit }) => {
    const canvasRef = React.useRef(null);
    const chartRef = React.useRef(null);
    const chartData = React.useMemo(() => buildStackedChartData(periods, columns), [periods, columns]);
    const canRender = hasRenderableSectionData(periods, columns);

    React.useEffect(() => {
        if (!canvasRef.current || !canRender) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }

            return undefined;
        }

        let cancelled = false;
        const canvas = canvasRef.current;

        const renderChart = async () => {
            const chartModule = await import("chart.js/auto");
            const Chart = chartModule.default;

            if (cancelled || !canvas) {
                return;
            }

            if (chartRef.current) {
                chartRef.current.destroy();
            }

            chartRef.current = new Chart(canvas, {
                type: "bar",
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: "index",
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: "top",
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => `${context.dataset.label}: ${formatOysterCount(context.raw, displayUnit)}`,
                                footer: (items) => {
                                    const total = items.reduce((sum, item) => sum + (Number(item.raw) || 0), 0);
                                    return `Total: ${formatOysterCount(total, displayUnit)}`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            stacked: true,
                        },
                        y: {
                            stacked: true,
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: `Oysters (${getDisplayUnitLabel(displayUnit)})`,
                            },
                            ticks: {
                                callback: (value) => formatDisplayQuantity(value, displayUnit),
                            },
                        },
                    },
                },
            });
        };

        renderChart();

        return () => {
            cancelled = true;

            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [canRender, chartData, displayUnit]);

    if (!canRender) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <div style={{ height: 420, position: "relative" }}>
            <canvas ref={canvasRef} />
        </div>
    );
};

const BatchReportTotalsChart = ({ periods, label, emptyMessage, displayUnit }) => {
    const canvasRef = React.useRef(null);
    const chartRef = React.useRef(null);
    const chartData = React.useMemo(() => buildTotalChartData(periods, label), [periods, label]);
    const canRender = hasRenderablePeriodTotals(periods);

    React.useEffect(() => {
        if (!canvasRef.current || !canRender) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }

            return undefined;
        }

        let cancelled = false;
        const canvas = canvasRef.current;

        const renderChart = async () => {
            const chartModule = await import("chart.js/auto");
            const Chart = chartModule.default;

            if (cancelled || !canvas) {
                return;
            }

            if (chartRef.current) {
                chartRef.current.destroy();
            }

            chartRef.current = new Chart(canvas, {
                type: "bar",
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: "index",
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: "top",
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => `${context.dataset.label}: ${formatOysterCount(context.raw, displayUnit)}`,
                            },
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: `Oysters (${getDisplayUnitLabel(displayUnit)})`,
                            },
                            ticks: {
                                callback: (value) => formatDisplayQuantity(value, displayUnit),
                            },
                        },
                    },
                },
            });
        };

        renderChart();

        return () => {
            cancelled = true;

            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [canRender, chartData, displayUnit]);

    if (!canRender) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <div style={{ height: 420, position: "relative" }}>
            <canvas ref={canvasRef} />
        </div>
    );
};

const BatchReportTable = ({ periods, columns, emptyMessage, displayUnit }) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Period</TableCell>
                        {columns.map((column) => (
                            <TableCell key={column.gradeId} align="right">{column.grade}</TableCell>
                        ))}
                        <TableCell align="right">Total ({getDisplayUnitLabel(displayUnit)})</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {periods.map((period) => (
                        <TableRow key={period.periodKey}>
                            <TableCell>
                                <Stack spacing={0.25}>
                                    <Typography variant="body2">{period.periodLabel}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDisplayDate(period.startDate)}{period.startDate !== period.endDate ? ` to ${formatDisplayDate(period.endDate)}` : ""}
                                    </Typography>
                                </Stack>
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell key={column.gradeId} align="right">
                                    {formatDisplayQuantity(getPeriodTotalValue(period, column.gradeId), displayUnit)}
                                </TableCell>
                            ))}
                            <TableCell align="right">{formatDisplayQuantity(period.totalOysters, displayUnit)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const BatchReportStockVsTransfersChart = ({ periods, columns, emptyMessage, displayUnit }) => {
    const canvasRef = React.useRef(null);
    const chartRef = React.useRef(null);
    const chartData = React.useMemo(() => buildStockVsTransfersChartData(periods, columns), [periods, columns]);
    const canRender = hasRenderableStockVsTransfersTotals(periods);

    React.useEffect(() => {
        if (!canvasRef.current || !canRender) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }

            return undefined;
        }

        let cancelled = false;
        const canvas = canvasRef.current;

        const renderChart = async () => {
            const chartModule = await import("chart.js/auto");
            const Chart = chartModule.default;

            if (cancelled || !canvas) {
                return;
            }

            if (chartRef.current) {
                chartRef.current.destroy();
            }

            chartRef.current = new Chart(canvas, {
                type: "bar",
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: "index",
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: "top",
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => `${context.dataset.label}: ${formatOysterCount(context.raw, displayUnit)}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            stacked: true,
                        },
                        y: {
                            stacked: true,
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: `Oysters (${getDisplayUnitLabel(displayUnit)})`,
                            },
                            ticks: {
                                callback: (value) => formatDisplayQuantity(value, displayUnit),
                            },
                        },
                    },
                },
            });
        };

        renderChart();

        return () => {
            cancelled = true;

            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [canRender, chartData, displayUnit]);

    if (!canRender) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <div style={{ height: 460, position: "relative" }}>
            <canvas ref={canvasRef} />
        </div>
    );
};

const BatchReportStockVsTransfersTable = ({ periods, columns, emptyMessage, displayUnit }) => {
    if (!Array.isArray(periods) || periods.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Stock Total ({getDisplayUnitLabel(displayUnit)})</TableCell>
                        <TableCell align="right">Cumulative Transfers ({getDisplayUnitLabel(displayUnit)})</TableCell>
                        {columns.flatMap((column) => ([
                            <TableCell key={`stock-${column.gradeId}`} align="right">Stock - {column.grade}</TableCell>,
                            <TableCell key={`transfer-${column.gradeId}`} align="right">Transfers - {column.grade}</TableCell>,
                        ]))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {periods.map((period) => (
                        <TableRow key={period.periodKey}>
                            <TableCell>
                                <Stack spacing={0.25}>
                                    <Typography variant="body2">{period.periodLabel}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDisplayDate(period.startDate)}{period.startDate !== period.endDate ? ` to ${formatDisplayDate(period.endDate)}` : ""}
                                    </Typography>
                                </Stack>
                            </TableCell>
                            <TableCell align="right">{formatDisplayQuantity(period.stockTotalOysters, displayUnit)}</TableCell>
                            <TableCell align="right">{formatDisplayQuantity(period.transferTotalOysters, displayUnit)}</TableCell>
                            {columns.flatMap((column) => ([
                                <TableCell key={`stock-value-${period.periodKey}-${column.gradeId}`} align="right">
                                    {formatDisplayQuantity(period.stockTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0, displayUnit)}
                                </TableCell>,
                                <TableCell key={`transfer-value-${period.periodKey}-${column.gradeId}`} align="right">
                                    {formatDisplayQuantity(period.transferTotals.find((item) => item.gradeId === column.gradeId)?.oysterCount || 0, displayUnit)}
                                </TableCell>,
                            ]))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const BatchReport = ({ access }) => {
    const [farm, setFarm] = React.useState(null);
    const [allowedFarmList, setAllowedFarmList] = React.useState([]);
    const [allFarms, setAllFarms] = React.useState(false);
    const [displayUnit, setDisplayUnit] = React.useState("doz");
    const [filters, setFilters] = React.useState(emptyFilters);
    const [periodType, setPeriodType] = React.useState("month");
    const [filterBatches, setFilterBatches] = React.useState(emptyFilterBatches);
    const [report, setReport] = React.useState(emptyReport);
    const [showStockVsTransfersGraph, setShowStockVsTransfersGraph] = React.useState(true);
    const [showReceivalGraph, setShowReceivalGraph] = React.useState(true);
    const [showBackToWaterGraph, setShowBackToWaterGraph] = React.useState(true);
    const [showTransferGraph, setShowTransferGraph] = React.useState(true);
    const [showUntrackedRemovalGraph, setShowUntrackedRemovalGraph] = React.useState(true);
    const [showGradeGraph, setShowGradeGraph] = React.useState(true);
    const [showVarianceGraph, setShowVarianceGraph] = React.useState(true);
    const [showOffSlatsGraph, setShowOffSlatsGraph] = React.useState(true);
    const [showMixedGraph, setShowMixedGraph] = React.useState(true);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [loadingReport, setLoadingReport] = React.useState(false);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");
    const resolvedFilterState = resolveFilterState(filterBatches, filters);
    const activeFilters = resolvedFilterState.filters;
    const filterOptions = resolvedFilterState.options;
    const stockVsTransfersColumns = React.useMemo(() => buildStockVsTransfersColumns(report), [report]);
    const stockVsTransfersPeriods = React.useMemo(
        () => buildStockVsTransfersPeriods(report, stockVsTransfersColumns),
        [report, stockVsTransfersColumns]
    );

    React.useEffect(() => {
        getAOCWarehouses(access, false, setAllowedFarmList);
    }, [access]);

    React.useEffect(() => {
        const abortController = new AbortController();
        const activeWarehouseId = allFarms ? undefined : farm?.warehouseId;

        if (!allFarms && !activeWarehouseId) {
            setFilterBatches(emptyFilterBatches);
            setFilters(emptyFilters);
            setPeriodType("month");
            setReport(emptyReport);
            setShowStockVsTransfersGraph(true);
            setShowReceivalGraph(true);
            setShowBackToWaterGraph(true);
            setShowTransferGraph(true);
            setShowUntrackedRemovalGraph(true);
            setShowGradeGraph(true);
            setShowVarianceGraph(true);
            setShowOffSlatsGraph(true);
            setShowMixedGraph(true);
            setHasSubmitted(false);
            setError("");
            return () => abortController.abort();
        }

        setLoadingFilters(true);
        setFilterBatches(emptyFilterBatches);
        setFilters(emptyFilters);
        setPeriodType("month");
        setReport(emptyReport);
        setShowStockVsTransfersGraph(true);
        setShowReceivalGraph(true);
        setShowBackToWaterGraph(true);
        setShowTransferGraph(true);
        setShowUntrackedRemovalGraph(true);
        setShowGradeGraph(true);
        setShowVarianceGraph(true);
        setShowOffSlatsGraph(true);
        setShowMixedGraph(true);
        setHasSubmitted(false);
        setError("");

        getAPIDataParamsReturn(
            "/Farms/BatchReporting/BatchReportFilters",
            { WarehouseId: activeWarehouseId },
            abortController
        )
            .then((data) => {
                setFilterBatches(normaliseBatchOptions(data?.batches));
            })
            .catch(() => {
                setFilterBatches(emptyFilterBatches);
                setError("Unable to load Batch Report filters.");
            })
            .finally(() => setLoadingFilters(false));

        return () => abortController.abort();
    }, [allFarms, farm?.warehouseId]);

    React.useEffect(() => {
        const filtersMatch = (
            filters.cropId === activeFilters.cropId
            && filters.supplierId === activeFilters.supplierId
            && filters.stockType === activeFilters.stockType
            && filters.cropDate === activeFilters.cropDate
        );

        if (filtersMatch) {
            return;
        }

        setFilters({
            cropId: activeFilters.cropId,
            supplierId: activeFilters.supplierId,
            stockType: activeFilters.stockType,
            cropDate: activeFilters.cropDate,
        });
    }, [
        filters.cropDate,
        filters.cropId,
        filters.stockType,
        filters.supplierId,
        activeFilters.cropDate,
        activeFilters.cropId,
        activeFilters.stockType,
        activeFilters.supplierId,
    ]);

    const handleFilterChange = (field) => (event) => {
        const nextValue = event.target.value;
        setFilters((current) => ({ ...current, [field]: nextValue }));
    };

    const handleSubmit = async () => {
        const activeWarehouseId = allFarms ? undefined : farm?.warehouseId;

        if (!allFarms && !activeWarehouseId) {
            return;
        }

        setLoadingReport(true);
        setHasSubmitted(true);
        setReport(emptyReport);
        setShowStockVsTransfersGraph(true);
        setShowReceivalGraph(true);
        setShowBackToWaterGraph(true);
        setShowTransferGraph(true);
        setShowUntrackedRemovalGraph(true);
        setShowGradeGraph(true);
        setShowVarianceGraph(true);
        setShowOffSlatsGraph(true);
        setShowMixedGraph(true);
        setError("");

        try {
            const data = await getAPIDataParamsReturn("/Farms/BatchReporting/BatchReport", {
                WarehouseId: activeWarehouseId,
                CropId: activeFilters.cropId || undefined,
                SupplierId: activeFilters.supplierId || undefined,
                StockType: activeFilters.stockType || undefined,
                CropDate: activeFilters.cropDate || undefined,
                PeriodType: periodType,
            });

            setReport(normaliseReport(data));
        }
        catch {
            setReport(emptyReport);
            setError("Unable to load the Batch Report.");
        }
        finally {
            setLoadingReport(false);
        }
    };

    const canLoad = allFarms || !!farm?.warehouseId;
    const filtersDisabled = !canLoad || loadingFilters;
    const stockVsTransfersHasData = hasRenderableStockVsTransfersTotals(stockVsTransfersPeriods);
    const backToWaterHasData = hasRenderableSectionData(report.backToWaterPeriods, report.backToWaterColumns);
    const transferHasData = hasRenderableSectionData(report.transferPeriods, report.transferColumns);
    const untrackedRemovalHasData = hasRenderableSectionData(report.untrackedRemovalPeriods, report.untrackedRemovalColumns);
    const gradeHasData = hasRenderableSectionData(report.gradePeriods, report.gradeColumns);
    const varianceHasData = hasRenderablePeriodTotals(report.variancePeriods);
    const offSlatsHasData = hasRenderablePeriodTotals(report.offSlatsPeriods);
    const mixedHasData = hasRenderablePeriodTotals(report.mixedPeriods);
    const backToWaterEmptyMessage = hasSubmitted
        ? "No back to water transfers matched the selected filters for this timeline."
        : "No back to water totals are available until the report is loaded.";
    const transferEmptyMessage = hasSubmitted
        ? (allFarms
            ? "No farm-to-non-farm transfers matched the selected filters for this timeline."
            : "No outbound transfers matched the selected filters for this timeline.")
        : "No transfer totals are available until the report is loaded.";
    const untrackedRemovalEmptyMessage = hasSubmitted
        ? "No untracked removals matched the selected filters for this timeline."
        : "No untracked removal totals are available until the report is loaded.";
    const displayUnitName = getDisplayUnitName(displayUnit);
    const topSummaryRows = React.useMemo(() => buildBatchReportTopSummaryRows(report), [report]);
    const hasMasterAccess = checkAccess(access, { key: ACCESS_SECTIONS.OYSTERMATE, value: ACCESS.MASTER });
    const masterReconciliation = React.useMemo(() => {
        const totalIn = Number(topSummaryRows.find((row) => row.id === "totalIn")?.total) || 0;
        const totalOut = Number(topSummaryRows.find((row) => row.id === "totalOut")?.total) || 0;
        const currentStock = Number(topSummaryRows.find((row) => row.id === "currentStock")?.total) || 0;
        const netFlow = totalIn - totalOut;

        return {
            netFlow,
            matchesCurrentStock: Math.abs(netFlow - currentStock) < 0.0001,
        };
    }, [topSummaryRows]);

    const handleExportStockVsTransfersTable = () => {
        const rows = buildStockVsTransfersTableRows(stockVsTransfersPeriods, stockVsTransfersColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("StockVsTransfers", report.periodType, displayUnit));
    };

    const handleExportGradeTable = () => {
        const rows = buildBatchReportTableRows(report.gradePeriods, report.gradeColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("GradeTotals", report.periodType, displayUnit));
    };

    const handleExportReceivalTable = () => {
        const rows = buildBatchReportTableRows(report.receivalPeriods, report.receivalColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("NewStock", report.periodType, displayUnit));
    };

    const handleExportBackToWaterTable = () => {
        const rows = buildBatchReportTableRows(report.backToWaterPeriods, report.backToWaterColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("BackToWater", report.periodType, displayUnit));
    };

    const handleExportTransferTable = () => {
        const rows = buildBatchReportTableRows(report.transferPeriods, report.transferColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("Transfers", report.periodType, displayUnit));
    };

    const handleExportUntrackedRemovalTable = () => {
        const rows = buildBatchReportTableRows(report.untrackedRemovalPeriods, report.untrackedRemovalColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("UntrackedRemovals", report.periodType, displayUnit));
    };

    const handleExportVarianceTable = () => {
        const rows = buildBatchReportTableRows(report.variancePeriods, [], displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("GradingVariance", report.periodType, displayUnit));
    };

    const handleExportOffSlatsTable = () => {
        const rows = buildBatchReportTableRows(report.offSlatsPeriods, [], displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("OffSlats", report.periodType, displayUnit));
    };

    const handleExportMixedTable = () => {
        const rows = buildBatchReportTableRows(report.mixedPeriods, report.mixedColumns, displayUnit);
        downloadCsvFile(rows, buildBatchReportExportFileName("MixedTotals", report.periodType, displayUnit));
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={5}>
                {!allFarms ? (
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
                ) : (
                    <Typography style={{ display: "inline" }} variant="h2" mb={0}>
                        Summary of All Farms
                    </Typography>
                )}
                {allowedFarmList.length > 0 ? (
                    <ToggleButton
                        color="primary"
                        style={{ marginLeft: "20px" }}
                        selected={allFarms}
                        onChange={() => setAllFarms((current) => !current)}
                    >
                        <div style={{ whiteSpace: "nowrap" }}>All Farms</div>
                    </ToggleButton>
                ) : null}
            </Grid>

            <Grid item xs={12} md={6} lg={7}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                    <Typography variant="body2" color="text.secondary">Display unit</Typography>
                    {DISPLAY_UNIT_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.id}
                            color="primary"
                            selected={displayUnit === option.id}
                            onChange={() => setDisplayUnit(option.id)}
                            value={option.id}
                        >
                            <div style={{ whiteSpace: "nowrap" }}>{option.label}</div>
                        </ToggleButton>
                    ))}
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Batch Report"
                        subheader="Filter root batches, choose a reporting period, then load the grade totals at each point in that batch timeline."
                    />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop"
                                    value={activeFilters.cropId}
                                    onChange={handleFilterChange("cropId")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crops</MenuItem>
                                    {filterOptions.crops.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {getCropLabel(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Supplier"
                                    value={activeFilters.supplierId}
                                    onChange={handleFilterChange("supplierId")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All suppliers</MenuItem>
                                    {filterOptions.suppliers.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Stock Type"
                                    value={activeFilters.stockType}
                                    onChange={handleFilterChange("stockType")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All stock types</MenuItem>
                                    {filterOptions.stockTypes.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop Date"
                                    value={activeFilters.cropDate}
                                    onChange={handleFilterChange("cropDate")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crop dates</MenuItem>
                                    {filterOptions.cropDates.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {formatDisplayDate(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Period"
                                    value={periodType}
                                    onChange={(event) => setPeriodType(event.target.value)}
                                    disabled={!canLoad || loadingReport}
                                >
                                    {PERIOD_OPTIONS.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={1}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={!canLoad || loadingFilters || loadingReport}
                                    sx={{ minHeight: 56 }}
                                >
                                    {loadingReport ? "Loading..." : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>

                        {loadingFilters ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading warehouse filters...</Typography>
                            </Stack>
                        ) : null}

                        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                            All oyster quantities on this page are displayed in {displayUnitName.toLowerCase()}.
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Batch Summary"
                        subheader="Quick stock-flow totals for the loaded report window."
                    />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading stock summary...</Typography>
                            </Stack>
                        ) : (
                            <BatchReportTopSummary
                                rows={topSummaryRows}
                                displayUnit={displayUnit}
                                showMasterDetail={hasMasterAccess}
                                masterReconciliation={masterReconciliation}
                                emptyMessage={hasSubmitted
                                    ? "No summary totals matched the selected filters for this timeline."
                                    : "Load the Batch Report to see total in, total out, and current stock."}
                            />
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Batch Totals by Grade" action={report.gradePeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportGradeTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowGradeGraph((current) => !current)} disabled={loadingReport}>
                                {showGradeGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading Batch Report...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    {hasSubmitted
                                        ? `Selected root batches: ${numberFormat(report.selectedBatchCount)} | Period: ${getPeriodTypeLabel(report.periodType)}`
                                        : "Choose any batch filters you want, select a period, and submit to load the report."}
                                </Typography>

                                {showGradeGraph ? (
                                    <BatchReportStackedChart
                                        periods={report.gradePeriods}
                                        columns={report.gradeColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No batch grade totals matched the selected filters for this timeline."
                                            : "No grade totals are available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.gradePeriods}
                                        columns={report.gradeColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No batch grade totals matched the selected filters for this timeline."
                                            : "No grade totals are available until the report is loaded."}
                                    />
                                )}

                                {gradeHasData ? (
                                    <Typography variant="caption" color="text.secondary">
                                        The main section excludes mixed totals that depend on parents outside the selected batch lineage.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Stock vs Transfers" action={stockVsTransfersPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportStockVsTransfersTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowStockVsTransfersGraph((current) => !current)} disabled={loadingReport}>
                                {showStockVsTransfersGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading stock vs transfers...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section compares the current batch stock-by-grade stack with a second stacked bar for each period showing cumulative transfers by grade up to that point.
                                </Typography>

                                {showStockVsTransfersGraph ? (
                                    <BatchReportStockVsTransfersChart
                                        periods={stockVsTransfersPeriods}
                                        columns={stockVsTransfersColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No stock or transfer totals matched the selected filters for this timeline."
                                            : "No stock vs transfers totals are available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportStockVsTransfersTable
                                        periods={stockVsTransfersPeriods}
                                        columns={stockVsTransfersColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No stock or transfer totals matched the selected filters for this timeline."
                                            : "No stock vs transfers totals are available until the report is loaded."}
                                    />
                                )}

                                {!stockVsTransfersHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Stock bars come from Batch Totals by Grade, while transfer bars accumulate the outbound transfer grades over the loaded timeline.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="New Stock" action={report.receivalPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportReceivalTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowReceivalGraph((current) => !current)} disabled={loadingReport}>
                                {showReceivalGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading new stock...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section shows new stock once, in the period where each selected lineage enters the report, so you can compare period inputs against later totals. Factory-to-farm back to water returns are excluded and shown separately below.
                                </Typography>

                                {showReceivalGraph ? (
                                    <BatchReportStackedChart
                                        periods={report.receivalPeriods}
                                        columns={report.receivalColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No new stock matched the selected filters for this timeline."
                                            : "No new stock is available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.receivalPeriods}
                                        columns={report.receivalColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No new stock matched the selected filters for this timeline."
                                            : "No new stock is available until the report is loaded."}
                                    />
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Back to Water" action={report.backToWaterPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportBackToWaterTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowBackToWaterGraph((current) => !current)} disabled={loadingReport}>
                                {showBackToWaterGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading back to water...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section shows stock that returned from factory to farm, bucketed into the period where the factory-to-farm transfer completed.
                                </Typography>

                                {showBackToWaterGraph ? (
                                    <BatchReportStackedChart
                                        periods={report.backToWaterPeriods}
                                        columns={report.backToWaterColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={backToWaterEmptyMessage}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.backToWaterPeriods}
                                        columns={report.backToWaterColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={backToWaterEmptyMessage}
                                    />
                                )}

                                {!backToWaterHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Only selected root groups that are linked to a completed factory-to-farm transfer line appear here.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Transfers from Farm" action={report.transferPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportTransferTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowTransferGraph((current) => !current)} disabled={loadingReport}>
                                {showTransferGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading transfers...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    {allFarms
                                        ? "This section shows transfers leaving the farm network by grade. Farm-to-farm transfers are excluded when All Farms is selected."
                                        : "This section shows outbound transfers by grade, including farm-to-farm and farm-to-non-farm transfers, bucketed into the same periods as the rest of the report."}
                                </Typography>

                                {showTransferGraph ? (
                                    <BatchReportStackedChart
                                        periods={report.transferPeriods}
                                        columns={report.transferColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={transferEmptyMessage}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.transferPeriods}
                                        columns={report.transferColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={transferEmptyMessage}
                                    />
                                )}

                                {!transferHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {allFarms
                                            ? "In All Farms mode, only transfers that leave the farm network appear here."
                                            : "Transfers only appear when outbound transfer lines match the current report filters."}
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Grading Variance" action={report.variancePeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportVarianceTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowVarianceGraph((current) => !current)} disabled={loadingReport}>
                                {showVarianceGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading grading variance...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section totals grading event variance by period. Each grading event contributes output oysters minus input oysters in the period where the grading outputs were created.
                                </Typography>

                                {showVarianceGraph ? (
                                    <BatchReportTotalsChart
                                        periods={report.variancePeriods}
                                        label="Variance oysters"
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No grading variance events matched the selected filters for this timeline."
                                            : "No grading variance totals are available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.variancePeriods}
                                        columns={[]}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No grading variance events matched the selected filters for this timeline."
                                            : "No grading variance totals are available until the report is loaded."}
                                    />
                                )}

                                {!varianceHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Variance only appears when grading events exist inside the selected batch lineage for the current report window.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Off Slats" action={report.offSlatsPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportOffSlatsTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowOffSlatsGraph((current) => !current)} disabled={loadingReport}>
                                {showOffSlatsGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading off slats...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section captures grading variance that is reclassified out of Grading Variance when the parent grading input is a slat grade.
                                </Typography>

                                {showOffSlatsGraph ? (
                                    <BatchReportTotalsChart
                                        periods={report.offSlatsPeriods}
                                        label="Off slats oysters"
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No off slats totals matched the selected filters for this timeline."
                                            : "No off slats totals are available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.offSlatsPeriods}
                                        columns={[]}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No off slats totals matched the selected filters for this timeline."
                                            : "No off slats totals are available until the report is loaded."}
                                    />
                                )}

                                {!offSlatsHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Off Slats only appears when grading variance events come from parent groups whose grade description contains Slat.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Untracked Removals" action={report.untrackedRemovalPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportUntrackedRemovalTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowUntrackedRemovalGraph((current) => !current)} disabled={loadingReport}>
                                {showUntrackedRemovalGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading untracked removals...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section shows leaf groups that end in the period without a linked completed transfer line. Transfer-linked endings stay in the tracked transfer sections instead of appearing here.
                                </Typography>

                                {showUntrackedRemovalGraph ? (
                                    <BatchReportStackedChart
                                        periods={report.untrackedRemovalPeriods}
                                        columns={report.untrackedRemovalColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={untrackedRemovalEmptyMessage}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.untrackedRemovalPeriods}
                                        columns={report.untrackedRemovalColumns}
                                        displayUnit={displayUnit}
                                        emptyMessage={untrackedRemovalEmptyMessage}
                                    />
                                )}

                                {!untrackedRemovalHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Only ended leaf groups with no matching completed transfer line are counted here.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Mixed Totals" action={report.mixedPeriods.length > 0 ? (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={handleExportMixedTable} disabled={loadingReport}>
                                Export CSV
                            </Button>
                            <Button variant="outlined" onClick={() => setShowMixedGraph((current) => !current)} disabled={loadingReport}>
                                {showMixedGraph ? "Show Table" : "Show Graph"}
                            </Button>
                        </Stack>
                    ) : null} />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading mixed totals...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    This section shows the mixed oyster total by period for descendants that mixed with any parent outside the selected batch lineage.
                                </Typography>

                                {showMixedGraph ? (
                                    <BatchReportTotalsChart
                                        periods={report.mixedPeriods}
                                        label="Mixed oysters"
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No external mixed totals were found for the selected filters."
                                            : "No mixed totals are available until the report is loaded."}
                                    />
                                ) : (
                                    <BatchReportTable
                                        periods={report.mixedPeriods}
                                        columns={[]}
                                        displayUnit={displayUnit}
                                        emptyMessage={hasSubmitted
                                            ? "No external mixed totals were found for the selected filters."
                                            : "No mixed totals are available until the report is loaded."}
                                    />
                                )}

                                {!mixedHasData && hasSubmitted ? (
                                    <Typography variant="caption" color="text.secondary">
                                        Mixed rows only appear when the selected batch lineage joins to a parent outside that selection.
                                    </Typography>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default BatchReport;